<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2021-2023 (original work) Open Assessment Technologies SA
    import { onMount, onDestroy } from 'svelte';
    import { selectedInteractions } from '@/store/interactions';
    import { OverlayBox, DataTable, Loading, Pagination } from '@oat-sa-private/ui-components';
    import { Button, ButtonAction, Icon, ScrollableWrapper } from '@oat-sa-private/ui-elements';
    import { __, remToPx, getActualKey, getLanguageName } from '@oat-sa-private/ui-core';
    import router from '@/core/router/router';
    import { actionsIcons, routes, tabKeys, timerLevels } from '@/core/utils/consts';
    import getInteractionUserName from '@/core/utils/getInteractionUserName';
    import { getTabLabel } from '@/core/utils/navigation';
    import RibbonContainer from '@/component/ui-elements/ribbonContainer/RibbonContainer.svelte';
    import NotificationManager from '@/component/ui-elements/notificationManager/NotificationManager.svelte';
    import { recoverableErrors } from '@/store/recoverableErrors';
    import getAvailableActions from '@/core/utils/getAvailableActions';
    import dynamicKeys from '@/locale/dynamicKeys.js';
    import { activeTab } from '@/store/navigation.js';
    import DurationCell from '@/component/ui-elements/durationCell/DurationCell.svelte';
    import { timestampToFinishTime } from '@/core/utils/parseInteractionsArray';
    import { getDxStatusLabel } from '@/core/utils/interactions.js';
    import { decodedAccessToken } from '@/store/accessToken';
    import actionToRouteKey from '@/core/utils/actionToRouteKey';
    import { getShowIPAddress } from '@/core/utils/ltiClaims';
    import { acsEventsStore } from '@/store/acsEvents';
    import { configurationStore } from '@/store/tenantConfiguration';
    import { formatMomentDatetime } from '@/core/utils/date.js';
    import BadgeCell from '@/component/ui-elements/badgeCell/BadgeCell.svelte';
    import AcsDetailsCell from '@/component/ui-elements/acsDetailsCell/AcsDetailsCell.svelte';

    const showIPAddress = getShowIPAddress($decodedAccessToken);
    const languageAccommodationTooltip = __('Interpreter assistance is being provided during this test');
    const defaultAcsLogColumns = [
        { id: 'incidentTime', visible: true },
        { id: 'action', visible: true },
        { id: 'actionStatus', visible: true },
        { id: 'actor', visible: true },
        { id: 'login', visible: true },
        { id: 'itemId', visible: true },
        { id: 'status', visible: true },
        { id: 'reasonMessage', visible: true }
    ];
    let acsEventsCurrentPage = 1;
    let acsEventsColumns = getAcsEventsColumns($configurationStore?.acsLogColumns);

    $: interaction = $selectedInteractions[0];

    $: availableActions = getAvailableActions($activeTab, [interaction]).map(acs => ({
        key: acs,
        label: dynamicKeys()[`acs-${acs}-label-singular`],
        icon: `${actionsIcons[acs]}-16`
    }));

    $: navigationDisabled = $recoverableErrors.internetConnection.status;
    $: acsEventsState = $acsEventsStore;
    $: acsEventsColumns = getAcsEventsColumns($configurationStore?.acsLogColumns);
    $: acsEventsCurrentPage = acsEventsState.pageNum + 1;
    $: acsEventsTotalPages = Math.ceil(acsEventsState.totalResults / acsEventsState.pageSize);
    $: acsEventsRows = ($configurationStore, acsEventsState.items.map(mapAcsEventToRow));
    $: acsEventsRangeStart =
        acsEventsState.totalResults === 0 ? 0 : acsEventsState.pageNum * acsEventsState.pageSize + 1;
    $: acsEventsRangeEnd = acsEventsState.pageNum * acsEventsState.pageSize + acsEventsState.items.length;
    $: showAcsEventsSection = acsEventsState.isLoading || acsEventsState.hasLoaded || Boolean(acsEventsState.error);
    $: syncAcsEventsColumnsSortOrder(acsEventsState.sortOrder);

    /**
     * Handles click on any action. At the moment it
     * just needs to redirect to the correct route,
     * if exists.
     * @param {string} action - name of the clicked action
     */
    function handleActionClicked(action) {
        action = actionToRouteKey(action);
        if (routes[action]) {
            router.redirect(routes[action]);
        }
    }

    /**
     * Handle OverlayBox event
     * @param {CustomEvent} e
     */
    function handleOverlayBoxAction(e) {
        if (e.detail.key === 'close') {
            closeOverlayBox();
        }
    }

    function closeOverlayBox() {
        // On closing /details we can only go to /
        router.redirect(routes.home);
    }

    /**
     * Handle key presses
     * @param {KeyboardEvent} event
     */
    function handleKeydown(event) {
        const keyname = getActualKey(event);
        if (keyname === 'esc') {
            event.stopPropagation();
            closeOverlayBox();
        }
    }

    // Calculations to provide correct height to ScrollableWrapper
    let windowHeight = 0;
    let detailsHeaderHeight = 0;
    const overlayBoxHeaderHeight = remToPx(5.5);
    const paddingsHeight = remToPx(3 + 3 + 1.75);
    $: nonScrollingHeight = detailsHeaderHeight + overlayBoxHeaderHeight + paddingsHeight;
    $: scrollableWrapperCssHeight = `${windowHeight - nonScrollingHeight}px`;

    function getAcsEventsColumns(configColumns = defaultAcsLogColumns) {
        const columnDefinitions = {
            incidentTime: { key: 'time', title: __('Time'), sortable: true, sortOrder: 'desc', lastSortOrder: 'desc' },
            action: { key: 'action', title: __('Action') },
            actionStatus: { key: 'result', title: __('Result'), cellComponent: BadgeCell },
            actor: { key: 'actor', title: __('Actor') },
            login: { key: 'login', title: __('Login') },
            itemId: { key: 'itemId', title: __('Item ID') },
            status: { key: 'state', title: __('State'), cellComponent: BadgeCell },
            reasonMessage: { key: 'details', title: __('Details'), cellComponent: AcsDetailsCell }
        };

        return configColumns
            .filter(column => column?.visible)
            .map(column => columnDefinitions[column.id])
            .filter(Boolean);
    }

    function updateHeaderSortOrder(columnKey, sortOrder) {
        acsEventsColumns = acsEventsColumns.map(column => {
            delete column.sortOrder;
            if (column.key === columnKey) {
                column.sortOrder = sortOrder;
                column.lastSortOrder = sortOrder;
            }
            return column;
        });
    }

    function syncAcsEventsColumnsSortOrder(sortOrder = 'DESC') {
        updateHeaderSortOrder('time', sortOrder.toLowerCase());
    }

    function loadAcsEventsPage(pageNum, sortOrder = acsEventsState.sortOrder) {
        if (!interaction?.deliveryExecution?.id) {
            return;
        }

        acsEventsStore.loadAcsEvents(interaction.deliveryExecution.id, {
            pageNum,
            sortOrder
        });
    }

    function handleAcsEventsSort({ detail: { key, sortOrder } }) {
        if (key !== 'time') {
            return;
        }

        updateHeaderSortOrder(key, sortOrder);
        loadAcsEventsPage(0, sortOrder.toUpperCase());
    }

    function handleAcsEventsPageChange({ detail: { page } }) {
        loadAcsEventsPage(page - 1);
    }

    function handleAcsEventsRetry() {
        loadAcsEventsPage(acsEventsState.pageNum, acsEventsState.sortOrder);
    }

    function handleAcsEventsRefresh() {
        loadAcsEventsPage(acsEventsState.pageNum, acsEventsState.sortOrder);
    }

    function formatAcsEventTime(incidentTime) {
        if (!incidentTime) {
            return '';
        }

        return formatMomentDatetime(incidentTime);
    }

    function formatAcsEventValue(value) {
        if (!value) {
            return '';
        }

        const acsEventValueLabels = {
            pause: __('Pause'),
            resume: __('Resume'),
            terminate: __('Terminate'),
            update: __('Update'),
            flag: __('Report'),
            start: __('Start'),
            submission: __('Submission'),
            navigation: __('Navigation'),
            termination: __('Termination'),
            log: __('Log'),
            authorize: __('Authorize'),
            proctor: __('Proctor'),
            'test-taker': __('Test taker'),
            system: __('System'),
            'deliver-fe': __('Delivery'),
            realtime: __('Realtime'),
            'security-plugin': __('Security plugin')
        };

        const key = String(value);
        if (Object.prototype.hasOwnProperty.call(acsEventValueLabels, key)) {
            return acsEventValueLabels[key];
        }

        return key.replace(/[_-]+/g, ' ').replace(/\b\w/g, character => character.toUpperCase());
    }

    function mapAcsEventResultBadge(actionStatus) {
        const resultMap = {
            succeeded: {
                text: __('Succeeded'),
                color: 'success'
            },
            failed: {
                text: __('Failed'),
                color: 'alert'
            },
            interacting: {
                text: __('In progress'),
                color: 'info'
            },
            closed: {
                text: __('Closed'),
                color: 'neutral'
            }
        };

        return (
            resultMap[actionStatus] || {
                text: formatAcsEventValue(actionStatus),
                color: 'neutral'
            }
        );
    }

    function mapAcsEventStateBadge(status) {
        const stateMap = {
            initial: {
                text: __('Awaiting authorization'),
                color: 'warning'
            },
            interacting: {
                text: __('In progress'),
                color: 'info'
            },
            suspended: {
                text: __('Paused'),
                color: 'warning'
            },
            closed: {
                text: __('Completed'),
                color: 'success'
            },
            terminated: {
                text: __('Terminated'),
                color: 'alert'
            },
            deleted: {
                text: __('Deleted'),
                color: 'neutral'
            }
        };

        return (
            stateMap[status] || {
                text: formatAcsEventValue(status),
                color: 'neutral'
            }
        );
    }

    function mapAcsEventToRow(event) {
        const rowData = {
            time: { data: formatAcsEventTime(event.incidentTime) },
            action: { data: formatAcsEventValue(event.action) },
            result: mapAcsEventResultBadge(event.actionStatus),
            actor: { data: formatAcsEventValue(event.actor) },
            login: { data: event.login || '' },
            itemId: { data: event.itemId || '' },
            state: mapAcsEventStateBadge(event.status),
            details: {
                data: event.reasonMessage || '',
                span: event.reasonCode ? `${__('Code')}: ${event.reasonCode}` : ''
            }
        };

        return acsEventsColumns.map(column => rowData[column.key]);
    }

    /**
     * Set up observer for dynamically measuring details-header height
     */
    const detailsHeaderObserver = new ResizeObserver(([entry]) => {
        const { height: currentHeight } = entry.target.getBoundingClientRect();
        if (currentHeight) {
            detailsHeaderHeight = currentHeight;
        }
    });

    let wrapperElement;
    let detailsHeaderElement;

    onMount(() => {
        wrapperElement.querySelector('.overlay-box')?.focus();

        detailsHeaderObserver.observe(detailsHeaderElement);
    });

    onDestroy(() => {
        detailsHeaderObserver.disconnect();
    });
</script>

<style>
    .wrapper {
        height: 100%;
        padding: 3rem 4rem;

        & :global(.overlay-box) {
            height: 100%;
        }
        & .info-container {
            padding: 1rem 0 0;
            height: 100%;

            & .details-header {
                display: flex;
                justify-content: space-between;
                align-items: baseline;
                margin: 0 2rem;

                & .info-title {
                    font-size: var(--fontsize-heading-l);
                    font-weight: bold;
                    margin-bottom: 3.375rem;
                }
            }
        }
        & :global(.overlay-box-content) {
            padding: 0;
        }
        & :global(.scrollable-wrapper) {
            padding: 2rem;
            display: flex;
            flex-direction: column;
        }
        & .details-container {
            background-color: var(--color-bg-info);
            padding: 3rem;
            margin-bottom: 2rem;
            display: flex;
            flex-wrap: wrap;
            gap: 0 6rem;

            & .language-content {
                display: inline-flex;
                align-items: center;
                gap: 0.5rem;
            }

            & .language-accommodation-trigger {
                display: inline-flex;
                align-items: center;
                padding: 0;
                border: 0;
                background: transparent;
                color: inherit;
                cursor: help;
            }
        }
        & .acs-events-container {
            margin-top: 2rem;
            display: flex;
            flex-direction: column;
            flex: 1 1 auto;
            min-height: 0;

            & .acs-events-empty {
                display: flex;
                flex: 1 1 auto;
                min-height: 0;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                text-align: center;
            }

            & .acs-events-empty-title {
                font-weight: bold;
                margin: 0 0 0.5rem;
            }

            & .acs-events-empty-message {
                margin: 0 0 1rem;
            }

            & .acs-events-error {
                display: flex;
                flex: 1 1 auto;
                min-height: 0;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                text-align: center;
            }

            & .acs-events-error-title {
                font-weight: bold;
                margin: 0 0 0.5rem;
            }

            & .acs-events-error-message {
                margin: 0 0 1rem;
            }

            & .acs-events-error :global(button) {
                width: auto;
            }

            & .acs-events-empty :global(button) {
                width: auto;
            }

            & .acs-events-table-container {
                overflow: auto;
                scrollbar-gutter: stable;
                flex: 1 1 auto;
                min-height: 0;

                & :global(table thead tr) {
                    position: sticky;
                    top: 0;
                    background: white;
                    z-index: var(--layer-2);
                }
            }

            & :global(table) {
                margin-bottom: 2rem;
                table-layout: fixed;
                width: 100%;
            }

            & :global(td),
            & :global(th) {
                vertical-align: top;
            }

            & .acs-events-footer {
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 1rem;
            }

            & .acs-events-pagination-info {
                margin: 0;
            }
        }
    }
</style>

<svelte:window bind:innerHeight={windowHeight} on:keydown={handleKeydown} />

<div class="wrapper" bind:this={wrapperElement}>
    <OverlayBox on:action={handleOverlayBoxAction}>
        {#if interaction}
            <div class="info-container">
                <div class="details-header" bind:this={detailsHeaderElement}>
                    <p class="info-title">
                        {__('Test taker')}:
                        {getInteractionUserName(interaction.user)}
                        {interaction.user?.sourcedId ? `(${interaction.user?.sourcedId})` : ''}
                    </p>
                    <div class="actions-container">
                        {#each availableActions as action}
                            <Button
                                label={action.label.toUpperCase()}
                                ariaLabel={action.label.toUpperCase()}
                                ariaHasPopup="dialog"
                                skin="secondary"
                                shape="pill"
                                size="small"
                                icon={action.icon}
                                disabled={navigationDisabled}
                                on:click={() => handleActionClicked(action.key)} />
                        {/each}
                    </div>
                </div>
                <ScrollableWrapper height={scrollableWrapperCssHeight}>
                    <div class="details-container">
                        <p><strong>{__('Status')}: </strong>{getTabLabel($activeTab)}</p>
                        <p><strong>{__('Battery / Delivery')}: </strong> {interaction.title || ''}</p>
                        {#if interaction.locale}
                            <p>
                                <strong>{__('Language')}: </strong>
                                <span class="language-content">
                                    <span>{getLanguageName(interaction.locale) || interaction.locale}</span>
                                    {#if interaction.flagLanguageAccommodation}
                                        <button
                                            type="button"
                                            class="language-accommodation-trigger"
                                            title={languageAccommodationTooltip}
                                            aria-label={languageAccommodationTooltip}>
                                            <Icon name="interpret-16" ariaHidden={true} />
                                        </button>
                                    {/if}
                                </span>
                            </p>
                        {/if}
                        {#if showIPAddress}
                            <p><strong>{__('IP address')}: </strong>{interaction.ipAddress || ''}</p>
                        {/if}

                        {#if $activeTab === tabKeys.inProgress && interaction.isTimerExists}
                            <p>
                                <strong>{__('Time remaining')}: </strong>
                                <DurationCell
                                    data={interaction.currentTimerLevel
                                        ? {
                                              startTs: 0,
                                              endTs: interaction.timers[interaction.currentTimerLevel].maxTimeRemaining,
                                              extra:
                                                  interaction.timers[timerLevels.extra]?.maxTime > 0
                                                      ? interaction.timers[timerLevels.extra]?.maxTimeRemaining
                                                      : null,
                                              timerLevel: interaction.currentTimerLevel,
                                              singleLine: true
                                          }
                                        : {}} />
                            </p>
                        {/if}

                        {#if ($activeTab === tabKeys.inProgress || $activeTab === tabKeys.paused) && interaction.isTimerExists}
                            <p>
                                <strong>{__('Additional time')}: </strong>
                                <DurationCell
                                    data={interaction.timers && interaction.timers[timerLevels.extra]?.maxTime > 0
                                        ? { startTs: 0, endTs: interaction.timers[timerLevels.extra].maxTime }
                                        : {}} />
                            </p>
                        {/if}

                        {#if $activeTab === tabKeys.finished}
                            <p><strong>{__('Cause')}: </strong> {getDxStatusLabel(interaction.deliveryExecution)}</p>
                            <p>
                                <strong>{__('Finish time')}: </strong>
                                {timestampToFinishTime(interaction.deliveryExecution.endTs) || ''}
                            </p>
                        {/if}

                        {#if $activeTab === tabKeys.inProgress || $activeTab === tabKeys.finished}
                            <p>
                                <strong>{__('Time spent')}: </strong><DurationCell
                                    data={{
                                        startTs: interaction.deliveryExecution.startTs,
                                        endTs: interaction.deliveryExecution.endTs
                                    }} />
                            </p>
                        {/if}

                        {#if $activeTab === tabKeys.inProgress || $activeTab === tabKeys.paused}
                            <p><strong>{__('Completion')}: </strong>{Math.round(interaction.progressPercentage)}%</p>
                            <p><strong>{__('Position')}: </strong>{interaction.positionLabel}</p>
                        {/if}
                    </div>

                    {#if showAcsEventsSection}
                        <div class="acs-events-container">
                            {#if acsEventsState.isLoading}
                                <Loading />
                            {:else if acsEventsState.error}
                                <div class="acs-events-error">
                                    <p class="acs-events-error-title">{__('Unable to load event history')}</p>
                                    <p class="acs-events-error-message">
                                        {__('Something went wrong while loading the data')}
                                    </p>
                                    <Button
                                        label={__('Retry')}
                                        skin="primary"
                                        shape="pill"
                                        on:click={handleAcsEventsRetry} />
                                </div>
                            {:else if acsEventsState.items.length === 0}
                                <div class="acs-events-empty">
                                    <p class="acs-events-empty-title">{__('No events yet')}</p>
                                    <p class="acs-events-empty-message">
                                        {__('Event history will appear once the session starts')}
                                    </p>
                                    <ButtonAction
                                        label={__('Refresh')}
                                        icon="refresh-16"
                                        skin="default"
                                        shape="pill"
                                        on:click={handleAcsEventsRefresh} />
                                </div>
                            {:else}
                                <div class="acs-events-table-container">
                                    <DataTable
                                        columns={acsEventsColumns}
                                        rows={acsEventsRows}
                                        fullwidth
                                        on:sort={handleAcsEventsSort} />
                                </div>

                                {#if acsEventsTotalPages > 1}
                                    <div class="acs-events-footer">
                                        <p class="acs-events-pagination-info">
                                            {__(
                                                'Showing %s-%s of %s',
                                                acsEventsRangeStart,
                                                acsEventsRangeEnd,
                                                acsEventsState.totalResults
                                            )}
                                        </p>
                                        <Pagination
                                            totalPages={acsEventsTotalPages}
                                            currentPage={acsEventsCurrentPage}
                                            on:changePage={handleAcsEventsPageChange} />
                                    </div>
                                {/if}
                            {/if}
                        </div>
                    {/if}
                </ScrollableWrapper>
            </div>
        {/if}
    </OverlayBox>
</div>

<RibbonContainer />
<NotificationManager />
